"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import posthog from "posthog-js";
import { useDropzone } from "react-dropzone";
import { Download, Loader2, Files, UserPlus, Scissors, Eraser } from "lucide-react";
import { SignedIn, SignedOut, useClerk } from "@clerk/nextjs";
import Link from "next/link";
import {
  convertFile,
  getFileData,
  type DeviceOverride,
  type FileSummary,
  type StripFields,
  type TrackPoint,
} from "@/lib/client-convert";
import type { HrFixRange } from "@/lib/hr-fix";
import { RouteMap } from "@/components/route-map";
import {
  ActivityCharts,
  HrFixPanel,
  SportChanger,
  DeviceChanger,
} from "@/components/lazy";
import { SplitsTable } from "@/components/splits-table";
import { TrimControl } from "@/components/trim-control";
import { FieldStripper } from "@/components/field-stripper";
import { TimeAdjuster } from "@/components/time-adjuster";
import { EditTools } from "@/components/edit-tools";
import { type TrimRange } from "@/lib/trim";
import {
  ACCEPTED_INPUT_TYPES,
  CONVERSION_TARGETS,
  getAvailableTargets,
  getDownloadName,
  inferTarget,
  type ConversionTarget,
} from "@/lib/formats";
import { buildActivityEdits, captureConversion } from "@/lib/edits";
import { triggerDownload } from "@/lib/download";
import { useEditedView } from "@/hooks/use-edited-view";
import { DownloadConfirmation } from "@/components/download-confirmation";
import { ProUpsellModal } from "@/components/pro-upsell-modal";
import { SignInGateModal } from "@/components/sign-in-gate-modal";
import { FloatingStatCard, useSimulatedRun } from "@/components/floating-stat-card";
import { AnimatedStatCard } from "@/components/animated-stat-card";
import { AffiliatePicks } from "@/components/affiliate-picks";
import { useProStatus } from "@/hooks/use-pro-status";
import { useAnonGate } from "@/hooks/use-anon-gate";
import {
  type UnitSystem,
  getDefaultUnitSystem,
  formatDistance,
  formatDuration,
} from "@/lib/units";

type Status = "idle" | "uploading" | "done" | "error";

export function Converter() {
  const [file, setFile] = useState<File | null>(null);
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState<string | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [target, setTarget] = useState<ConversionTarget>("fit");
  const [summary, setSummary] = useState<FileSummary | null>(null);
  const [points, setPoints] = useState<TrackPoint[]>([]);
  const [trim, setTrim] = useState<TrimRange>({ startSec: 0, endSec: 0 });
  const [strip, setStrip] = useState<StripFields>({});
  const [timeShiftMs, setTimeShiftMs] = useState(0);
  const [sportOverride, setSportOverride] = useState<string | undefined>(undefined);
  const [deviceOverride, setDeviceOverride] = useState<DeviceOverride | undefined>(undefined);
  const [hrFixRanges, setHrFixRanges] = useState<HrFixRange[]>([]);
  const [units, setUnits] = useState<UnitSystem>("metric");
  const [showUpsell, setShowUpsell] = useState(false);
  const { showSignInGate, setShowSignInGate, checkAnonGate, recordAnonConversion } = useAnonGate();
  const isPro = useProStatus();
  const { openSignUp } = useClerk();
  const sim = useSimulatedRun();

  useEffect(() => {
    setUnits(getDefaultUnitSystem());
  }, []);

  useEffect(() => {
    const reset = () => {
      setFile(null);
      setDownloadUrl(null);
      setError(null);
      setStatus("idle");
      setSummary(null);
      setPoints([]);
    };
    window.addEventListener("reset-converter", reset);
    return () => window.removeEventListener("reset-converter", reset);
  }, []);

  // Reset all per-activity edits (trim + the five editors) for each new file.
  useEffect(() => {
    setTrim({ startSec: 0, endSec: 0 });
    setStrip({});
    setTimeShiftMs(0);
    setSportOverride(undefined);
    setDeviceOverride(undefined);
    setHrFixRanges([]);
  }, [points]);

  // Any edit invalidates an already-produced download, so drop back to idle.
  const markEditsDirty = useCallback(() => {
    setStatus((s) => (s === "done" ? "idle" : s));
    setDownloadUrl((u) => {
      if (u) URL.revokeObjectURL(u);
      return null;
    });
  }, []);

  const addHrFixRange = useCallback(
    (range: HrFixRange) => {
      setHrFixRanges((prev) => [...prev, range]);
      markEditsDirty();
    },
    [markEditsDirty]
  );

  const removeHrFixRange = useCallback(
    (index: number) => {
      setHrFixRanges((prev) => prev.filter((_, i) => i !== index));
      markEditsDirty();
    },
    [markEditsDirty]
  );

  const view = useEditedView(points, trim, hrFixRanges);

  const disabled = useMemo(() => status === "uploading", [status]);

  const reset = useCallback(() => {
    setFile(null);
    setDownloadUrl(null);
    setError(null);
    setStatus("idle");
    setSummary(null);
    setPoints([]);
  }, []);

  const handleFileSelected = useCallback((f: File) => {
    if (checkAnonGate()) return;
    setFile(f);
    setTarget(inferTarget(f));
    setError(null);
    setDownloadUrl(null);
    setStatus("idle");
    setSummary(null);
    setPoints([]);
    getFileData(f)
      .then((data) => {
        setSummary(data.summary);
        setPoints(data.points);
      })
      .catch(() => {
        setSummary(null);
        setPoints([]);
      });
  }, [checkAnonGate]);

  const handleConvert = useCallback(
    async () => {
      setError(null);
      setDownloadUrl(null);

      if (!file) {
        setError("Please choose a TCX or FIT file first.");
        return;
      }

      const allowedTargets = getAvailableTargets(file);
      if (!allowedTargets.includes(target)) {
        setError("That conversion isn't available for this file type.");
        return;
      }

      if (checkAnonGate()) return;

      try {
        setStatus("uploading");
        const edits = buildActivityEdits(
          points,
          { trim, strip, timeShiftMs, sportOverride, deviceOverride, hrFixRanges },
          isPro
        );
        const blob = await convertFile(file, target, edits);

        const url = URL.createObjectURL(blob);
        setDownloadUrl(url);
        setStatus("done");

        captureConversion(posthog, summary?.format ?? "unknown", target, edits);

        triggerDownload(url, getDownloadName(file, target));

        recordAnonConversion();

        if (!isPro) {
          const count = Number(sessionStorage.getItem("fitconvert-conversions") || "0") + 1;
          sessionStorage.setItem("fitconvert-conversions", String(count));
          if (count >= 2 && !sessionStorage.getItem("fitconvert-upsell-shown")) {
            sessionStorage.setItem("fitconvert-upsell-shown", "1");
            setShowUpsell(true);
          }
        }
      } catch (err) {
        setStatus("error");
        setError(err instanceof Error ? err.message : "Something went wrong");
      }
    },
    [file, target, isPro, checkAnonGate, recordAnonConversion, trim, strip, timeShiftMs, sportOverride, deviceOverride, hrFixRanges, points, summary]
  );

  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    onDrop: (acceptedFiles) => {
      const f = acceptedFiles?.[0];
      if (f) handleFileSelected(f);
    },
    disabled,
    multiple: false,
    accept: ACCEPTED_INPUT_TYPES,
    noClick: true,
    noKeyboard: true,
  });

  // If no file is loaded, show the hero landing page
  if (!file) {
    return (
      <>
        <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 lg:py-24 relative overflow-hidden">
          {/* Background glow */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/5 rounded-full blur-[120px] pointer-events-none" />

          {/* Hero content */}
          <div className="max-w-4xl w-full text-center mb-16 relative z-10">
            <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-6">
              Don&apos;t Leave <br />
              <span className="text-primary">Your Hard-Earned PRs</span> Behind.
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Keep your history intact across every platform. <br className="hidden sm:inline" />
              A workout unrecorded is a workout forgotten.
            </p>
          </div>

          {/* Central converter area */}
          <div className="w-full max-w-6xl flex flex-col lg:flex-row items-center justify-center gap-8 relative z-20">
            {/* Left floating cards */}
            <div className="hidden lg:flex flex-col gap-6 -translate-x-8">
              <FloatingStatCard
                className="-rotate-3"
                icon={<svg className="size-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><path d="M18 6L6 18M6 6l12 12" /></svg>}
                label="Distance"
                value={sim.distance}
              />
              <FloatingStatCard
                className="rotate-2 translate-x-4"
                icon={<svg className="size-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>}
                label="Avg Pace"
                value={sim.pace}
              />
            </div>

            {/* Main upload card */}
            <div {...getRootProps()} className="w-full max-w-xl bg-slate-900/40 rounded-2xl border border-white/5 shadow-2xl p-8 backdrop-blur-sm">
              <input {...getInputProps()} aria-label="Select fitness file to convert" />
              <div
                className={`border-2 border-dashed rounded-xl p-10 flex flex-col items-center justify-center bg-white/5 group cursor-pointer transition-colors ${
                  isDragActive ? "border-primary/60 bg-primary/5" : "border-white/10 hover:border-primary/40"
                }`}
                onClick={() => { if (!checkAnonGate()) open(); }}
              >
                <div className="size-16 rounded-full bg-primary/10 flex items-center justify-center text-primary mb-4 group-hover:scale-110 transition-transform">
                  <svg className="size-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                </div>
                <h3 className="text-lg font-bold mb-1">Convert and trim your file here</h3>
                <p className="text-sm text-muted-foreground mb-6">Convert between FIT, TCX, GPX, and CSV in seconds</p>
                <button
                  type="button"
                  className="bg-primary text-primary-foreground px-8 py-3 rounded-lg font-bold text-sm shadow-lg shadow-primary/20 hover:shadow-primary/40 transition-all"
                  onClick={(e) => { e.stopPropagation(); if (!checkAnonGate()) open(); }}
                >
                  Select Performance File
                </button>
              </div>
            </div>

            {/* Right floating cards */}
            <div className="hidden lg:flex flex-col gap-6 translate-x-8">
              <FloatingStatCard
                className="rotate-3"
                icon={<svg className="size-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" /></svg>}
                label="Avg HR"
                value={sim.hr}
              />
              <FloatingStatCard
                className="-rotate-2 -translate-x-4"
                icon={<svg className="size-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" /></svg>}
                label="Avg Power"
                value={sim.power}
              />
            </div>
          </div>
        </div>

        <SignedOut>
          <div className="w-full max-w-xl mx-auto px-6 pb-8">
            <div className="rounded-xl p-6 glass-panel border border-primary/10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="p-2.5 bg-primary/10 rounded-lg shrink-0">
                <UserPlus className="size-5 text-primary" />
              </div>
              <p className="flex-1 min-w-0 text-sm">Sign up to convert and repair unlimited files for free.</p>
              <button
                type="button"
                onClick={() => openSignUp()}
                className="shrink-0 bg-primary text-primary-foreground px-5 py-2 rounded-lg text-sm font-bold hover:brightness-110 transition-all shadow-lg shadow-primary/20"
              >
                Sign up free
              </button>
            </div>
          </div>
        </SignedOut>
        <SignedIn>
          {!isPro && (
            <div className="w-full max-w-xl mx-auto px-6 pb-8">
              <div className="rounded-xl p-6 glass-panel border border-primary/10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="p-2.5 bg-primary/10 rounded-lg shrink-0">
                  <Files className="size-5 text-primary" />
                </div>
                <p className="flex-1 min-w-0 text-sm">Upgrade to Pro for $2.99/mo to enable batch conversion.</p>
                <Link
                  href="/pricing"
                  className="shrink-0 bg-primary text-primary-foreground px-5 py-2 rounded-lg text-sm font-bold hover:brightness-110 transition-all shadow-lg shadow-primary/20"
                >
                  Upgrade to Pro
                </Link>
              </div>
            </div>
          )}
        </SignedIn>

        <ProUpsellModal open={showUpsell} onOpenChange={setShowUpsell} />
        <SignInGateModal open={showSignInGate} onOpenChange={setShowSignInGate} />
      </>
    );
  }

  // Post-download: a full-width confirmation screen (no sidebar).
  if (status === "done" && downloadUrl) {
    return (
      <>
        <DownloadConfirmation
          fileName={getDownloadName(file, target)}
          onDownloadAgain={() => triggerDownload(downloadUrl, getDownloadName(file, target))}
          onReset={reset}
        />
        <ProUpsellModal open={showUpsell} onOpenChange={setShowUpsell} />
      </>
    );
  }

  // Dashboard view: file is loaded. `view` is the (optionally trimmed) slice
  // and its derived stats, driving the map, charts, stats and splits below.
  const { viewPoints, positions } = view;
  const hasMap = summary?.hasGps && positions.length > 0;
  const availableTargets = getAvailableTargets(file);

  return (
    <>
      <div className="flex-1 flex flex-col lg:flex-row p-6 gap-6 max-w-screen-2xl mx-auto w-full">
        {/* Left sidebar */}
        <aside className="w-full lg:w-80 flex flex-col gap-6">
          <div className="rounded-xl p-5 glass-panel">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-primary/10 rounded-lg">
                <svg className="size-5 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
              </div>
              <div>
                <h3 className="font-bold">Active File</h3>
                <p className="text-xs text-muted-foreground">Ready for conversion</p>
              </div>
            </div>
            <div className="p-4 bg-background rounded-lg border border-border mb-6">
              <p className="text-sm font-medium truncate">{file.name}</p>
              <p className="text-xs mt-1 flex items-center gap-1">
                <svg className="size-3 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
                <span className="text-muted-foreground">Status: Ready</span>
              </p>
            </div>
            <div className="mb-6">
              <label className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3 block">Export as</label>
              <div className="grid grid-cols-2 gap-2">
                {CONVERSION_TARGETS.map((opt) => {
                  const available = availableTargets.includes(opt);
                  return (
                    <button
                      key={opt}
                      type="button"
                      disabled={!available || disabled}
                      onClick={() => {
                        setTarget(opt);
                        if (status === "done") {
                          setStatus("idle");
                          setDownloadUrl(null);
                        }
                      }}
                      className={`py-2 px-3 text-sm font-medium border rounded-lg transition-colors ${
                        target === opt
                          ? "border-primary bg-primary/10 text-primary"
                          : available
                          ? "border-border hover:border-muted-foreground"
                          : "border-border opacity-30 cursor-not-allowed"
                      }`}
                    >
                      {opt.toUpperCase()}
                    </button>
                  );
                })}
              </div>
            </div>
            <button
              type="button"
              onClick={handleConvert}
              disabled={disabled}
              className="w-full py-3 bg-primary text-primary-foreground font-bold rounded-lg hover:brightness-110 transition-all flex items-center justify-center gap-2 mb-3 shadow-lg shadow-primary/20"
            >
              {status === "uploading" ? (
                <><Loader2 className="size-4 animate-spin" /> Converting...</>
              ) : (
                <><Download className="size-4" /> Download File</>
              )}
            </button>
            <button
              type="button"
              onClick={reset}
              className="w-full py-2 text-sm text-muted-foreground font-medium hover:text-foreground transition-colors"
            >
              Change File
            </button>
            {error && <p className="text-sm text-destructive mt-3">{error}</p>}
          </div>
        </aside>

        {/* Right main area */}
        <section className="flex-1 flex flex-col gap-6">
          {/* File header */}
          <div className="rounded-xl p-6 glass-panel">
            <h1 className="text-3xl font-black tracking-tight mb-3">{file.name}</h1>
            <div className="flex flex-wrap gap-6 items-center">
              {points.length > 0 && points[0].timeMs > 0 && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <svg className="size-4 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
                  <span className="font-medium">Start Time:</span>
                  {" "}{new Date(points[0].timeMs).toLocaleDateString(undefined, {
                    month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
                  })}
                </div>
              )}
              {summary && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <svg className="size-4 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" /><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" /></svg>
                  <span className="font-medium">Trackpoints:</span> {summary.points.toLocaleString()}
                </div>
              )}
              {summary && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <svg className="size-4 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
                  <span className="font-medium">File Type:</span> {summary.format.toUpperCase()} {summary.sport || "Activity"}
                </div>
              )}
            </div>
          </div>

          {/* Map + stats grid */}
          {summary && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {hasMap && (
                <div className="lg:col-span-2 relative h-[400px] rounded-xl overflow-hidden border border-white/5 glass-panel">
                  <RouteMap positions={positions} />
                  <div className="absolute top-4 right-4 bg-background/80 backdrop-blur-md px-3 py-1.5 rounded-lg border border-border z-10">
                    <p className="text-xs font-bold flex items-center gap-2">
                      <svg className="size-3 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" /></svg>
                      GPS PREVIEW
                    </p>
                  </div>
                </div>
              )}

              {/* Summary stats */}
              <div className={`grid grid-cols-2 ${hasMap ? "lg:grid-cols-1" : "lg:col-span-3 lg:grid-cols-2 xl:grid-cols-4"} gap-4`}>
                {view.distanceM > 0 && (
                  <AnimatedStatCard label="Distance" value={formatDistance(view.distanceM, units)} />
                )}
                {view.durationMs > 0 && (
                  <AnimatedStatCard label="Duration" value={formatDuration(view.durationMs)} />
                )}
                {view.avgHr !== null && (
                  <AnimatedStatCard label="Avg Heart Rate" value={`${view.avgHr}`} unit="bpm" />
                )}
                {view.hasElevation && (
                  <AnimatedStatCard label="Elevation Gain" value={`${view.elevGainM}`} unit="m" />
                )}
              </div>
            </div>
          )}

          {/* Charts */}
          {viewPoints.length > 0 && summary && (
            <ActivityCharts points={viewPoints} summary={summary} unitSystem={units} />
          )}

          {/* Splits (free) */}
          {viewPoints.length > 0 && <SplitsTable points={viewPoints} unitSystem={units} />}

          {/* All editors hidden behind an "Edit my file" disclosure */}
          {summary && (
            <EditTools>
              {/* Change sport (free) */}
              <SportChanger
                currentSport={summary.sport}
                value={sportOverride}
                onChange={(next) => {
                  setSportOverride(next);
                  markEditsDirty();
                }}
              />

              {/* Change device (free) */}
              <DeviceChanger
                currentManufacturer={summary.manufacturer}
                value={deviceOverride}
                onChange={(next) => {
                  setDeviceOverride(next);
                  markEditsDirty();
                }}
              />

              {/* Fix heart rate glitches (Pro) — gates itself, and renders
                  nothing at all when the file has no HR data. */}
              <HrFixPanel
                points={points}
                isPro={isPro}
                hrFixRanges={hrFixRanges}
                onAddRange={addHrFixRange}
                onRemoveRange={removeHrFixRange}
              />

              {/* Trim (Pro) — gate non-Pro users behind an upgrade teaser */}
              {points.length > 1 && (
                isPro ? (
                  <TrimControl
                    points={points}
                    value={view.range}
                    onChange={(next) => {
                      setTrim(next);
                      markEditsDirty();
                    }}
                  />
                ) : (
                  <Link
                    href="/pricing"
                    className="rounded-xl p-6 glass-panel border border-primary/10 flex items-center gap-4 transition hover:border-primary/30"
                  >
                    <div className="p-2.5 bg-primary/10 rounded-lg shrink-0">
                      <Scissors className="size-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold">Trim this activity</p>
                      <p className="text-sm text-muted-foreground">
                        Crop out warm-ups or a forgotten stop before converting — available on Pro.
                      </p>
                    </div>
                    <span className="shrink-0 bg-primary text-primary-foreground px-4 py-2 rounded-lg text-sm font-bold">
                      Upgrade
                    </span>
                  </Link>
                )
              )}

              {/* Remove fields, adjust time (Pro) */}
              {isPro ? (
                <>
                  <FieldStripper
                    summary={summary}
                    value={strip}
                    onChange={(next) => {
                      setStrip(next);
                      markEditsDirty();
                    }}
                    target={target}
                    onSwitchTarget={(next) => {
                      setTarget(next);
                      if (status === "done") {
                        setStatus("idle");
                        setDownloadUrl(null);
                      }
                    }}
                  />
                  <TimeAdjuster
                    startMs={summary.startMs}
                    value={timeShiftMs}
                    onChange={(next) => {
                      setTimeShiftMs(next);
                      markEditsDirty();
                    }}
                  />
                </>
              ) : (
                <Link
                  href="/pricing"
                  className="rounded-xl p-6 glass-panel border border-primary/10 flex items-center gap-4 transition hover:border-primary/30"
                >
                  <div className="p-2.5 bg-primary/10 rounded-lg shrink-0">
                    <Eraser className="size-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold">Remove fields &amp; adjust time</p>
                    <p className="text-sm text-muted-foreground">
                      Strip GPS, heart rate or power before sharing, and fix time zones or clock drift — available on Pro.
                    </p>
                  </div>
                  <span className="shrink-0 bg-primary text-primary-foreground px-4 py-2 rounded-lg text-sm font-bold">
                    Upgrade
                  </span>
                </Link>
              )}
            </EditTools>
          )}

          <SignedOut>
            <div className="rounded-xl p-6 glass-panel border border-primary/10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="p-2.5 bg-primary/10 rounded-lg shrink-0">
                <UserPlus className="size-5 text-primary" />
              </div>
              <p className="flex-1 min-w-0 text-sm">Sign up to convert and repair unlimited files for free.</p>
              <button
                type="button"
                onClick={() => openSignUp()}
                className="shrink-0 bg-primary text-primary-foreground px-5 py-2 rounded-lg text-sm font-bold hover:brightness-110 transition-all shadow-lg shadow-primary/20"
              >
                Sign up free
              </button>
            </div>
          </SignedOut>
          <SignedIn>
            {!isPro && (
              <div className="rounded-xl p-6 glass-panel border border-primary/10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="p-2.5 bg-primary/10 rounded-lg shrink-0">
                  <Files className="size-5 text-primary" />
                </div>
                <p className="flex-1 min-w-0 text-sm">Upgrade to Pro for $2.99/mo to enable batch conversion.</p>
                <Link
                  href="/pricing"
                  className="shrink-0 bg-primary text-primary-foreground px-5 py-2 rounded-lg text-sm font-bold hover:brightness-110 transition-all shadow-lg shadow-primary/20"
                >
                  Upgrade to Pro
                </Link>
              </div>
            )}
          </SignedIn>

          {/* Affiliate picks at the bottom of the preview */}
          {summary && <AffiliatePicks count={4} />}
        </section>
      </div>

      <ProUpsellModal open={showUpsell} onOpenChange={setShowUpsell} />
    </>
  );
}


